import { useMemo } from 'react'

import { Empty } from '@/components/lemmala-design-system/empty/empty'
import { useAgGridContext } from '@/components/lemmala-design-system/table'
import { TABLE_EMPTY_STATE_LABELS } from '@/components/lemmala-design-system/table/labels'
import { type EmptyStateButton } from '@/components/lemmala-design-system/table/types'

type AgTableEmptyStateProps = {
  headerMessage?: string
  subHeaderMessage?: string
}

export const AgTableEmptyState = ({ headerMessage, subHeaderMessage }: AgTableEmptyStateProps) => {
  const context = useAgGridContext()
  const searchValue = context?.searchValue
  const filterModel = context?.filterModel
  const currentViewName = context?.currentViewName
  const viewFilterModel =
    currentViewName && context?.getViewDefinition
      ? context.getViewDefinition(currentViewName).filterModel
      : undefined
  const hasUserFilters = Object.keys(filterModel ?? {}).some(
    (key) => !viewFilterModel || !(key in viewFilterModel),
  )
  const hasActiveFilters = Boolean(searchValue) || hasUserFilters
  const entityName = context?.entityName
  const contextHeaderMessage = context?.headerEmptyStateMessage
  const contextSubHeaderMessage = context?.subHeaderEmptyStateMessage
  const emptyStateDefinition = context?.emptyStateDefinition
  const emptyStateButton = context?.emptyStateButton

  const { button, variant } = useMemo(() => {
    const resolveAction = (action?: EmptyStateButton) => {
      if (action?.onClick && context) {
        return () => action.onClick?.(context)
      }

      return () => {}
    }

    const resolveVariant = () => {
      if (!hasActiveFilters) {
        return emptyStateDefinition?.zeroData
      }

      if (typeof emptyStateDefinition?.filtered === 'function') {
        return emptyStateDefinition.filtered({ filterModel })
      }

      return emptyStateDefinition?.filtered
    }

    const resolvedVariant = resolveVariant()
    let button = undefined

    if (resolvedVariant?.button) {
      button = {
        label: resolvedVariant.button.label,
        onClick: resolveAction(resolvedVariant.button),
      }
    } else if (emptyStateButton) {
      button = {
        label: emptyStateButton.label,
        onClick: resolveAction(emptyStateButton),
      }
    }

    return {
      variant: resolvedVariant,
      button,
    }
  }, [context, emptyStateButton, emptyStateDefinition, filterModel, hasActiveFilters])

  // Use props first, then context, then defaults
  const finalHeaderMessage =
    variant?.title ??
    headerMessage ??
    contextHeaderMessage ??
    TABLE_EMPTY_STATE_LABELS.defaultHeader
  const finalSubHeaderMessage =
    variant?.description ??
    subHeaderMessage ??
    contextSubHeaderMessage ??
    TABLE_EMPTY_STATE_LABELS.defaultSubHeader

  if (hasActiveFilters && !variant && searchValue) {
    return (
      <Empty
        icon='secondary'
        title={TABLE_EMPTY_STATE_LABELS.noResultsTitle}
        subtitle={TABLE_EMPTY_STATE_LABELS.noResultsDescription(searchValue, entityName)}
      />
    )
  }

  return (
    <Empty
      icon={hasActiveFilters ? 'primary' : 'secondary'}
      title={finalHeaderMessage}
      subtitle={finalSubHeaderMessage}
      button={button}
    />
  )
}
